import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import type { PermissionName } from 'c-sdk';
import { useUserPermissions } from 'c-auth-module/Hooks';
import ConditionalDebugWrapper from 'c-auth-module/Components/ConditionalDebugWrapper';
import type { ConditionalDebugWrapperProps } from '../ConditionalDebugWrapper/ConditionalDebugWrapper';

type Props = PropsWithChildren<
    {
        permissions?: PermissionName[];
        // Only use if children are already conditionally shown depending on permissions.
        forceShow?: boolean;
    } & Omit<ConditionalDebugWrapperProps, 'show' | 'tooltipLines'>
>;

const IfHasAllPermissions: React.FC<Props> = ({ permissions, forceShow, ...rest }) => {
    const { hasAll } = useUserPermissions();
    const show = useMemo(
        () => forceShow || permissions == null || permissions?.length <= 0 || hasAll(permissions),

        [hasAll, permissions, forceShow],
    );

    const lines = useMemo(() => (permissions ?? []).map(p => p), [permissions]);

    const hideDebugWrapper = useMemo(
        () => permissions == null || permissions?.length <= 0,
        [permissions],
    );
    if (hideDebugWrapper) return <>{rest.children}</>;

    return <ConditionalDebugWrapper show={show} tooltipLines={lines} {...rest} />;
};

export default React.memo(IfHasAllPermissions);
